// 能源畫面的 react-query hooks 與執行環境：mock 世界、選單可見條件（NRG-V1）、範圍 → 查詢、單位與格式。
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../lib/AppContext';
import { useScope } from '../../lib/scope';
import { isSimple } from '../shell/mycamMode';
import { configureEnergy, energyApi, energyMode } from './api';
import { makeFmt, type EnergyUnit, type Fmt } from './format';
import { energyMockMode } from './mockMode';
import { energyTargets, mergeSummaries, type EnergyTarget } from './model';
import type { EnergySummary, Period } from './types';

/** 每次 render 設好 api.ts 的 mock 世界（mock 打開時用範圍切換器的家戶與組織）。 */
export function useEnergyEnv(): void {
  const { config } = useApp();
  const { homes, orgs, tree } = useScope();
  const mode = energyMockMode(config);
  // 家戶的場域路徑（範圍樹是深度優先的平面清單：home 列上面、深度較淺的 site 列就是它的路徑）
  const pathOf = useMemo(() => {
    const m = new Map<string, string>();
    const stack: { depth: number; label: string }[] = [];
    for (const n of tree) {
      while (stack.length && stack[stack.length - 1].depth >= n.depth) stack.pop();
      if (n.kind === 'home') m.set(n.key.replace(/^home:/, ''), stack.map((x) => x.label).join(' › '));
      else if (n.kind === 'site') stack.push({ depth: n.depth, label: n.label });
      else if (n.kind === 'org') stack.length = 0;
    }
    return m;
  }, [tree]);
  configureEnergy(
    mode,
    homes
      .filter((h) => !h.external && h.org_id)
      .map((h) => ({ home_id: h.home_id, name: h.display_name ?? h.home_id, org_id: h.org_id!, site_id: h.site_id, tz: h.timezone ?? 'Asia/Taipei', role: h.role, path: pathOf.get(h.home_id) })),
    orgs.filter((o) => !o.notice).map((o) => ({ org_id: o.org_id, name: o.display_name, role: o.role })),
  );
}

/**
 * 選單「用電」出現條件（NRG-V1）：對任一家戶有 energy.read 而且那個家戶有至少一個量測點，不看目前範圍。
 * energy.read：設備的 actions 帶（分享 / 委派 / site 授權勾的），或家戶角色 owner / admin / member 隱含（guest 不隱含）。
 * 量測點：插座、Station（PoE 每孔）、帶 electrical_* 能力的設備（第三方 Matter 插座 / 電表經中樞）。
 * mock 打開時一律出現（看畫面用）。
 */
export function useEnergyNav(): boolean {
  const { config } = useApp();
  const { devices, homes } = useScope();
  return useMemo(() => {
    if (energyMockMode(config) !== 'off') return homes.some((h) => !h.external);
    const roleOf = new Map(homes.map((h) => [h.home_id, h.role]));
    return devices.some((d) => {
      const meterish = d.category === 'plug' || d.category === 'station' || (d.children ?? []).some((c) => (c as { category?: string }).category === 'plug');
      if (!meterish) return false;
      if (d.actions.some((a) => a.action === 'energy.read')) return true;
      const r = roleOf.get(d.home_id);
      return r === 'owner' || r === 'admin' || r === 'member';
    });
  }, [config, devices, homes]);
}

/** 用電單位：team 組織寫 kWh；個人組織與簡易模式寫「度」（SPEC #energy-terms）。 */
export function useEnergyUnit(): EnergyUnit {
  const { config, uiMode } = useApp();
  const { org, visible } = useScope();
  if (isSimple(config, uiMode)) return 'du';
  const kind = org?.kind ?? (visible.length && visible.every((h) => h.org_kind === 'personal') ? 'personal' : 'team');
  return kind === 'personal' ? 'du' : 'kwh';
}

export function useFmt(): Fmt {
  const { t, i18n } = useTranslation();
  const unit = useEnergyUnit();
  return useMemo(() => makeFmt(t, i18n.language, unit), [t, i18n.language, unit]);
}

/** 目前範圍要查的組織與 scope。 */
export function useEnergyTargets(): EnergyTarget[] {
  const { scope, visible, homes, orgs, planes } = useScope();
  return useMemo(
    () =>
      energyTargets(
        scope,
        visible.map((h) => ({ home_id: h.home_id, org_id: h.org_id, api: h.endpoints?.api, external: h.external })),
        homes.map((h) => ({ home_id: h.home_id, org_id: h.org_id, api: h.endpoints?.api, external: h.external })),
        (org) => orgs.find((o) => o.org_id === org)?.api ?? planes[0]?.api,
      ),
    [scope, visible, homes, orgs, planes],
  );
}

/** 目前範圍的摘要（逐組織查、合併）。 */
export function useScopeSummary(period: Period, date?: string) {
  useEnergyEnv();
  const targets = useEnergyTargets();
  const qs = useQueries({
    queries: targets.map((tg) => ({
      queryKey: ['energy', 'summary', energyMode(), tg.api, tg.org, tg.scope, period, date ?? ''],
      queryFn: () => energyApi.orgSummary(tg.api, { org: tg.org, scope: tg.scope, period, date }),
      staleTime: 60_000,
      retry: false,
    })),
  });
  const loading = qs.some((q) => q.isLoading);
  const ok = qs.filter((q) => q.data).map((q) => q.data as EnergySummary);
  const failed = qs.filter((q) => q.isError);
  const data = useMemo(() => mergeSummaries(ok), [ok.map((s) => s).join('|'), ...qs.map((q) => q.dataUpdatedAt)]); // eslint-disable-line react-hooks/exhaustive-deps
  return {
    targets,
    loading,
    data,
    error: failed.length === qs.length && qs.length > 0 ? failed[0].error : null,
    partialError: failed.length > 0 && failed.length < qs.length,
    refetch: () => qs.forEach((q) => void q.refetch()),
  };
}
